import React, { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"
import { DEF_PG_SIZE_SUB } from '../../constants/maximum-page-size'
import { useLocale } from "../../contexts/localeContext"
import { TableContextProvider } from "../../contexts/table/TableContextProvider"
import { TranslationKeys } from "../../contexts/TranslationKeys"
import columns from "../../models/ColumnDefinitions/CardTypeColumns"
import CardTypeNameColumns from "../../models/ColumnDefinitions/CardTypeNameColumn"
import { ListData } from "../../models/ListData"
import { ListQuery } from "../../models/ListQuery"
import { Lookup } from "../../models/Lookup"
import LookupTypes from "../../models/LookupTypes"
import searchColumns from "../../models/search/CardTypeSearch"
import SiteSearch from "../../models/search/SiteSearch"
import { TabSummary } from "../../models/TabSummary"
import { getAllCardTypes, getCardTypes, updateCardType } from "../../services/cardTypeService"
import { getSiteNamesWithPagination } from "../../services/siteService"
import { showError } from "../../services/utilities/ServiceError"
import ActivityFrame from "../activity-frame/activity-frame"
import AssignWizard from "../assignWizard/AssignWizard"
import TableTab from "../tabs/table-tab"

interface Props {
    itemId: string,
    tabs: TabSummary[],
    setTabs: (tabs: TabSummary[]) => void
}

const CardTypes: FC<Props> = ({ itemId, tabs, setTabs }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.assignedTypes);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [oldSelectedIds, setOldSelectedIds] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);

    useEffect(() => {
        dataRefresh((query) => getCardTypeFormats(query));
    },[itemId])

    useEffect(() => {
        setLookup([{
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        }]);
    }, [])


    useEffect(() => {
        setIds();
        dataRefresh((query) => getCardTypeFormats(query));
    }, [showAddModal])

    const setIds = async () => {
        try {
            const cardTypes = await getCardTypes(itemId) as any;
            const cardTypeIds = cardTypes?.rows.map(cardType => cardType.id);
            setSelectedIds(cardTypeIds);
            setOldSelectedIds(cardTypeIds);
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const assignCardTypes = async () => {
        const newSelectedIds = selectedIds.filter(value => value !== "");
        setOldSelectedIds(newSelectedIds);

        const toBeRemoved = oldSelectedIds.filter(value => !newSelectedIds.includes(value));
        const toBeAdded = newSelectedIds.filter(value => !oldSelectedIds.includes(value));

        setBusy(true);

        try {
            await Promise.all([
                ...toBeRemoved.map(id => updateCardType({truncationFormatId:'50', cardTypeId: id.toString()})),
                ...toBeAdded.map(id => updateCardType({truncationFormatId: itemId, cardTypeId:id.toString()}))
            ])
            toast.success(translate.byKey(TranslationKeys.cardTypesUpdated));
            setTabs([{...tabs[0], badge:newSelectedIds.length}])
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setBusy(false);
        }

    }
    
    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });
    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    const getCardTypeFormats = (listParameters) => {
        return getCardTypes(itemId,listParameters);
    }

    return (
        <>
            <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white'>
                <TableContextProvider
                    dataMethod={(listParameters) => getCardTypeFormats(listParameters)}
                    tableDescriptor={columns}
                    searchDescriptor={searchColumns}
                    useTable
                    usePagination
                    useSearch
                    useSort
                    manualDataRefresh={manualDataRefresh}
                    useFilter
                    maxPageSize={DEF_PG_SIZE_SUB}
                    lookup={lookup}>
                    <TableTab
                        title={title}
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                        selectRows={false}
                        cyName="cardType">
                        <button type="button"
                            className="flex ml-8 px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                            onClick={() => setShowAddModal(true)}>
                            {translate.byKey(TranslationKeys.manage)}
                        </button>
                    </TableTab>
                </TableContextProvider>
                <AssignWizard
                    isOpen={showAddModal}
                    dataMethod={getAllCardTypes}
                    lookup={lookup}
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                    onClose={() => { setShowAddModal(false) }}
                    title={translate.byKey(TranslationKeys.assignCardTypes)}
                    columnDescriptors={
                        {
                            columns: CardTypeNameColumns,
                            searchColumns: searchColumns,
                        }
                    }
                    handleOnSubmit={assignCardTypes} />
            </ActivityFrame>
        </>
    )
};

export default CardTypes;
